import { ActionableNotification, InlineNotification } from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import { useEffect, useRef } from 'react';

const DISMISS_AFTER_MS = 8000;

/**
 * A success or warning that goes away by itself (no close button, like the errors in admin-ui). `onDone` clears the
 * state that shows it. It stays a status region so assistive technology announces it once.
 */
export function TransientNotice({
  kind,
  title,
  onDone,
}: {
  kind: 'success' | 'warning';
  title: string;
  onDone(): void;
}) {
  const done = useRef(onDone);
  useEffect(() => {
    done.current = onDone;
  });
  useEffect(() => {
    const timer = window.setTimeout(() => done.current(), DISMISS_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [title]);
  return <InlineNotification kind={kind} role="status" lowContrast hideCloseButton title={title} />;
}

/** An error that stays until it is fixed, with Retry where something can be fetched again. */
export function ErrorNotice({ title, onRetry }: { title: string; onRetry?(): void }) {
  const { t } = useTranslation();
  if (!onRetry) return <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={title} />;
  return (
    <ActionableNotification
      inline
      kind="error"
      role="alert"
      lowContrast
      hideCloseButton
      title={title}
      actionButtonLabel={t('admin.retry')}
      onActionButtonClick={onRetry}
    />
  );
}
